import type { ErrorInfo } from "react";

export type NextjsReportableError = Error & { digest?: string };

/** Suppress duplicate reports from React Strict Mode remounts without blocking reset. */
const REPORT_DEDUP_WINDOW_MS = 100;

const reportedErrors = new Map<string, number>();

export function __resetReportedNextjsErrorsForTests(): void {
  reportedErrors.clear();
}

function pruneStaleReportedErrors(now: number): void {
  for (const [key, reportedAt] of reportedErrors) {
    if (now - reportedAt >= REPORT_DEDUP_WINDOW_MS) {
      reportedErrors.delete(key);
    }
  }
}

function shouldReportError(key: string, now = Date.now()): boolean {
  pruneStaleReportedErrors(now);

  const lastReportedAt = reportedErrors.get(key);
  if (
    lastReportedAt !== undefined &&
    now - lastReportedAt < REPORT_DEDUP_WINDOW_MS
  ) {
    return false;
  }

  reportedErrors.set(key, now);
  return true;
}

function getNextjsErrorKey(error: NextjsReportableError): string {
  if (error.digest) {
    return `digest:${error.digest}`;
  }

  const firstStackLine = error.stack?.split("\n", 2)[1]?.trim();
  return `error:${error.message}:${firstStackLine ?? ""}`;
}

/**
 * Reports a Next.js error to Datadog RUM.
 *
 * Uses a short dedup window by default to absorb React Strict Mode remounts.
 * Callers supply `report` so this module stays free of `@datadog/browser-rum-nextjs`
 * imports and can be used from isomorphic Pages Router code.
 */
export function reportNextjsErrorToRum(
  error: NextjsReportableError,
  report: (error: Error, errorInfo?: ErrorInfo) => void,
  errorInfo?: ErrorInfo,
  options?: { dedupe?: boolean },
): void {
  if (options?.dedupe !== false) {
    const key = getNextjsErrorKey(error);
    if (!shouldReportError(key)) {
      return;
    }
  }

  report(error, errorInfo);
}
